/**
 * Centralized formatters for user-facing name display.
 *
 * Walkabout displays first names only on user-facing surfaces (profiles,
 * connections, messaging, lists, etc.) to align with the product's focus on
 * real-world, in-person connection and to avoid unnecessary privacy exposure.
 *
 * Use `getDisplayName` for any surface that shows another user's name.
 * Use `getFullName` only where a legal/full name is genuinely required (e.g.
 * the user's own profile, ticket/will-call entries, contact form pre-fill).
 */

export interface NameFormatterUser {
  name?: string | null;
  surname?: string | null;
  handle?: string | null;
}

const FALLBACK_DISPLAY_NAME = 'Neighbor';

/**
 * Returns the preferred user-facing display name for a user.
 * Prefers first name, falls back to handle, then to a generic label.
 */
export const getDisplayName = (user?: NameFormatterUser | null): string => {
  const name = user?.name?.trim();
  if (name) return name;
  const handle = user?.handle?.trim();
  if (handle) return handle;
  return FALLBACK_DISPLAY_NAME;
};

/**
 * Returns the user's full name. Only use where a legal/full name is required
 * (own profile, tickets, will-call, contact-form pre-fill). For all other
 * surfaces use `getDisplayName`.
 */
export const getFullName = (user?: NameFormatterUser | null): string => {
  const full = `${user?.name ?? ''} ${user?.surname ?? ''}`.trim();
  if (full) return full;
  const handle = user?.handle?.trim();
  if (handle) return handle;
  return FALLBACK_DISPLAY_NAME;
};
